import { useState, useCallback } from 'react';
import { FileDown, FileText, Table as TableIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/use-toast';
import { formatDateTime, formatDuration } from '@/lib/utils';
import type { MigrationSummary } from '@/types';

interface ExportMenuProps {
  migrations: MigrationSummary[];
  className?: string | undefined;
}

const STATUS_LABELS: Record<string, string> = {
  InProgress: 'In Progress',
  Succeeded: 'Succeeded',
  Partial: 'Partial Success',
  Failed: 'Failed',
};

export function ExportMenu({ migrations, className }: ExportMenuProps) {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const prepareExportData = useCallback(() => {
    return migrations.map((m) => ({
      'Organization': m.organizationName,
      'Type': m.organizationType === 'New' ? 'New Organization' : 'Existing Organization',
      'Status': STATUS_LABELS[m.status] ?? m.status,
      'Started By': m.startedByUserName,
      'Started At': formatDateTime(m.startedAt),
      'Completed At': formatDateTime(m.completedAt),
      'Duration': formatDuration(m.startedAt, m.completedAt),
      'Total Modules': m.totalModules,
      'Completed Modules': m.completedModules,
      'Failed Modules': m.failedModules,
    }));
  }, [migrations]);

  const handleExportCsv = useCallback(() => {
    setIsExporting(true);
    try {
      const data = prepareExportData();
      if (data.length === 0) {
        toast({
          title: 'No Data',
          description: 'There are no migrations to export.',
          variant: 'destructive',
        });
        return;
      }

      // Create CSV content
      const firstRow = data[0];
      if (!firstRow) {
        toast({
          title: 'No Data',
          description: 'There are no migrations to export.',
          variant: 'destructive',
        });
        return;
      }
      const headers = Object.keys(firstRow);
      const csvRows = [
        headers.join(','),
        ...data.map((row) =>
          headers
            .map((header) => {
              const value = String(row[header as keyof typeof row] ?? '');
              // Escape quotes and wrap in quotes if contains comma or quote
              if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value;
            })
            .join(',')
        ),
      ];
      const csvContent = csvRows.join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `migrations-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Export Complete',
        description: `Exported ${data.length} migrations to CSV.`,
      });
    } catch (error) {
      console.error('Failed to export CSV:', error);
      toast({
        title: 'Export Failed',
        description: 'There was an error exporting the data.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  }, [prepareExportData, toast]);

  const handleExportExcel = useCallback(async () => {
    setIsExporting(true);
    try {
      const data = prepareExportData();
      if (data.length === 0) {
        toast({
          title: 'No Data',
          description: 'There are no migrations to export.',
          variant: 'destructive',
        });
        return;
      }

      // Create XLSX-compatible content (simple XML format)
      const firstRow = data[0];
      if (!firstRow) {
        toast({
          title: 'No Data',
          description: 'There are no migrations to export.',
          variant: 'destructive',
        });
        return;
      }
      const headers = Object.keys(firstRow);

      // Build XML content for Excel
      let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n';
      xmlContent += '<?mso-application progid="Excel.Sheet"?>\n';
      xmlContent += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n';
      xmlContent += '  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';
      xmlContent += '  <Worksheet ss:Name="Migrations">\n';
      xmlContent += '    <Table>\n';

      // Header row
      xmlContent += '      <Row>\n';
      headers.forEach((header) => {
        xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(header)}</Data></Cell>\n`;
      });
      xmlContent += '      </Row>\n';

      // Data rows
      data.forEach((row) => {
        xmlContent += '      <Row>\n';
        headers.forEach((header) => {
          const value = row[header as keyof typeof row];
          const type = typeof value === 'number' ? 'Number' : 'String';
          xmlContent += `        <Cell><Data ss:Type="${type}">${escapeXml(String(value ?? ''))}</Data></Cell>\n`;
        });
        xmlContent += '      </Row>\n';
      });

      xmlContent += '    </Table>\n';
      xmlContent += '  </Worksheet>\n';
      xmlContent += '</Workbook>';

      // Download file
      const blob = new Blob([xmlContent], {
        type: 'application/vnd.ms-excel',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `migrations-${new Date().toISOString().slice(0, 10)}.xls`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Export Complete',
        description: `Exported ${data.length} migrations to Excel.`,
      });
    } catch (error) {
      console.error('Failed to export Excel:', error);
      toast({
        title: 'Export Failed',
        description: 'There was an error exporting the data.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  }, [prepareExportData, toast]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={className}
          disabled={isExporting || migrations.length === 0}
        >
          {isExporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileDown className="mr-2 h-4 w-4" />
          )}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleExportCsv} disabled={isExporting}>
          <FileText className="mr-2 h-4 w-4" />
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportExcel} disabled={isExporting}>
          <TableIcon className="mr-2 h-4 w-4" />
          Export as Excel
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
